import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { ChevronLeft, Download, FileSpreadsheet, Truck, PieChart, Wallet } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { reportsAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import AgentBottomNav from '../agent/AgentBottomNav';
import { toast } from 'react-toastify';

const tabs = [
  { id: 'payments', label: 'Payment report', Icon: Wallet },
  { id: 'deliveries', label: 'Bill delivery', Icon: Truck },
  { id: 'delivery_summary', label: 'Delivery summary', Icon: PieChart },
  { id: 'payment_summary', label: 'Payment summary', Icon: FileSpreadsheet },
];

const REPORT_COLUMNS = {
  payments: [
    { key: 'payment_date', label: 'Payment date & time' },
    { key: 'valuation_no', label: 'Valuation no.' },
    { key: 'bill_no', label: 'Bill no.' },
    { key: 'property_owner', label: 'Property owner' },
    { key: 'payer_name', label: 'Payer name' },
    { key: 'bill_amount', label: 'Bill amount' },
    { key: 'amount_paid', label: 'Amount paid' },
    { key: 'agent_name', label: 'Agent name' },
    { key: 'collection_unit', label: 'Collection unit' },
    { key: 'payment_method', label: 'Payment method' },
  ],
  deliveries: [
    { key: 'delivery_date', label: 'Delivery date & time' },
    { key: 'valuation_no', label: 'Valuation no.' },
    { key: 'property_owner', label: 'Property owner' },
    { key: 'recipient_name', label: 'Recipient name' },
    { key: 'recipient_number', label: 'Recipient phone' },
    { key: 'bill_amount', label: 'Bill amount' },
    { key: 'agent_name', label: 'Delivering agent' },
    { key: 'assigned_agent_name', label: 'Assigned agent' },
    { key: 'delivery_location', label: 'Property / delivery location' },
    { key: 'distance_from_property_m', label: 'Distance from property (m)' },
    { key: 'collection_unit', label: 'Collection unit' },
  ],
  delivery_summary: [
    { key: 'collection_unit', label: 'Collection unit' },
    { key: 'agent_name', label: 'Agent name' },
    { key: 'total_bills', label: 'Total bills' },
    { key: 'bills_delivered', label: 'Bills delivered' },
  ],
  payment_summary: [
    { key: 'collection_unit', label: 'Collection unit' },
    { key: 'agent_name', label: 'Agent name' },
    { key: 'total_bills', label: 'Total bills' },
    { key: 'total_bill_value', label: 'Total bill value' },
    { key: 'total_payments', label: 'Total payments' },
  ],
};

function ReportsHub() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const back =
    location.pathname.includes('/supervisor/')
      ? '/supervisor/dashboard'
      : location.pathname.includes('/agent/')
        ? '/agent/dashboard'
        : '/admin/dashboard';

  const [tab, setTab] = useState('payments');
  const [collectionUnit, setCollectionUnit] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [units, setUnits] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const columns = useMemo(() => REPORT_COLUMNS[tab] || [], [tab]);

  useEffect(() => {
    (async () => {
      try {
        const u = await reportsAPI.getCollectionUnitsMeta();
        setUnits(Array.isArray(u) ? u : []);
      } catch {
        setUnits([]);
      }
    })();
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const q = { collectionUnit: collectionUnit || undefined, from: from || undefined, to: to || undefined };
      let data;
      if (tab === 'payments') data = await reportsAPI.payments(q);
      else if (tab === 'deliveries') data = await reportsAPI.deliveries(q);
      else if (tab === 'delivery_summary') data = await reportsAPI.deliverySummary({ from: from || undefined, to: to || undefined });
      else data = await reportsAPI.paymentSummary({ from: from || undefined, to: to || undefined });
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error('Failed to load report');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [tab, collectionUnit, from, to]);

  useEffect(() => {
    load();
  }, [load]);

  const showCuFilter = tab === 'payments' || tab === 'deliveries';
  const isSuperAdmin = user?.userType === 'admin' || user?.userType === 'super_admin';

  const handleExportCsv = useCallback(() => {
    if (!rows.length) {
      toast.info('No rows to export');
      return;
    }

    const escapeCsv = (value) => {
      const str = value == null ? '' : String(value);
      return /[",\n]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str;
    };

    const headerRow = columns.map((col) => escapeCsv(col.label)).join(',');
    const bodyRows = rows.map((row) =>
      columns.map((col) => escapeCsv(formatCell(row[col.key], col))).join(','),
    );
    const csv = [headerRow, ...bodyRows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const stamp = new Date().toISOString().slice(0, 10);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${tab}-${stamp}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }, [rows, columns, tab]);

  return (
    <div className="h-full flex flex-col min-h-0">
      <header className="p-4 md:p-5 lg:p-6 shrink-0 bg-gradient-to-r from-[#001E3C] to-[#000B18] border-b border-white/10">
        <div className="flex items-center gap-3 md:gap-4 mb-4">
          <button type="button" onClick={() => navigate(back)} className="glass-card p-3 rounded-xl">
            <ChevronLeft className="w-5 h-5 text-white" />
          </button>
          <div>
            <h2 className="text-lg md:text-xl font-black uppercase text-white">Reports</h2>
            <p className="text-xs text-white/50">Scoped to your role</p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {tabs.map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => setTab(t.id)}
              className={
                'px-2.5 md:px-3 py-1.5 md:py-2 rounded-lg text-[11px] md:text-xs font-black uppercase flex items-center gap-1.5 ' +
                (tab === t.id ? 'bg-[#9C27B0] text-white' : 'bg-white/10 text-white/70')
              }
            >
              <t.Icon className="w-3.5 h-3.5" />
              {t.label}
            </button>
          ))}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 mt-4">
          {showCuFilter && (
            <div className="lg:col-span-1">
              <label className="block text-xs font-bold uppercase text-white/50 mb-1">Collection unit</label>
              <select
                value={collectionUnit}
                onChange={(e) => setCollectionUnit(e.target.value)}
                className="w-full p-2.5 rounded-xl bg-white/10 border border-white/20 text-white text-sm"
              >
                <option value="">All collection units (default)</option>
                {units.map((u) => (
                  <option key={u} value={String(u)}>
                    Unit {u}
                  </option>
                ))}
              </select>
            </div>
          )}
          <div className={showCuFilter ? 'lg:col-span-1' : 'lg:col-span-2'}>
            <p className="text-xs font-bold uppercase text-white/50 mb-1">Date range (optional)</p>
            <div className="flex gap-2 items-center">
              <label className="sr-only">From date</label>
              <input
                type="date"
                value={from}
                onChange={(e) => setFrom(e.target.value)}
                className="flex-1 min-w-0 p-2.5 rounded-xl bg-white/10 border border-white/20 text-white text-sm"
                title="Leave empty for no start filter"
              />
              <span className="text-white/40 text-sm shrink-0">to</span>
              <label className="sr-only">To date</label>
              <input
                type="date"
                value={to}
                onChange={(e) => setTo(e.target.value)}
                className="flex-1 min-w-0 p-2.5 rounded-xl bg-white/10 border border-white/20 text-white text-sm"
                title="Leave empty for no end filter"
              />
            </div>
            <p className="text-[10px] text-white/40 mt-1">Empty dates show all periods. Times use your browser locale.</p>
          </div>
          <div className={showCuFilter ? 'lg:col-span-2 flex flex-wrap items-center gap-2' : 'lg:col-span-2 flex flex-wrap items-center gap-2'}>
            <button
              type="button"
              onClick={load}
              className="w-full md:w-auto md:min-w-[220px] py-2.5 px-4 rounded-xl bg-[#00F5FF]/20 text-[#00F5FF] text-xs font-bold uppercase"
            >
              Apply filters / refresh
            </button>
            {isSuperAdmin && (
              <button
                type="button"
                onClick={handleExportCsv}
                className="w-full md:w-auto md:min-w-[160px] inline-flex justify-center items-center gap-1.5 px-3 py-2.5 rounded-xl bg-[#FFC107]/20 text-[#FFC107] text-xs font-bold uppercase"
                title="Export current report rows to CSV"
              >
                <Download className="w-3.5 h-3.5" />
                Export CSV
              </button>
            )}
          </div>
        </div>
      </header>
      <main className="flex-1 min-h-0 overflow-auto p-3 md:p-4 no-scrollbar">
        {loading ? (
          <p className="text-white/60 text-sm p-4">Loading...</p>
        ) : rows.length === 0 ? (
          <p className="text-white/60 text-sm p-4">No rows.</p>
        ) : (
          <div className="overflow-x-auto rounded-xl border border-white/10">
            <table className="min-w-full text-left text-[11px] text-white/90 border-collapse">
              <thead>
                <tr className="bg-white/5 border-b border-white/20">
                  {columns.map((col) => (
                    <th key={col.key} className="p-2.5 whitespace-nowrap font-black uppercase text-[#FFC107] text-[10px]">
                      {col.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={i} className="border-b border-white/10 hover:bg-white/[0.03]">
                    {columns.map((col) => (
                      <td
                        key={col.key}
                        className="p-2.5 max-w-[220px] align-top"
                        title={String(r[col.key] ?? '')}
                      >
                        <span className="break-words">{formatCell(r[col.key], col)}</span>
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>
      {user?.userType === 'agent' ? <AgentBottomNav active="reports" /> : null}
    </div>
  );
}

function formatCell(v, col) {
  if (v == null || v === '') return '�';
  if (col?.key === 'distance_from_property_m' && typeof v === 'number') {
    return `${Math.round(v)} m`;
  }
  if (col?.key === 'collection_unit' && typeof v === 'number') {
    return String(v);
  }
  if (typeof v === 'number') {
    if (col?.key === 'bill_amount' || col?.key === 'amount_paid') return Number(v).toFixed(2);
    return Number.isInteger(v) ? String(v) : Number(v).toFixed(2);
  }
  if (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(v)) {
    try {
      return new Date(v).toLocaleString();
    } catch {
      return v;
    }
  }
  return String(v);
}

export default ReportsHub;


